import {Device} from "@capacitor/device";
import {App} from "@capacitor/app";
import {SafeArea} from 'capacitor-plugin-safe-area';
import {SecureStoragePlugin} from "capacitor-secure-storage-plugin";
import {GameDispatcher} from "../pixi";

export class DeviceDetectorService {

  private userAgent: string;

  constructor() {
    this.userAgent = navigator.userAgent || '';
  }

  public isMobile(): boolean {
    return /Mobi|Android|iPhone|iPod|IEMobile|Opera Mini/i.test(this.userAgent);
  }

  public isTablet(): boolean {
    return /iPad|Tablet|PlayBook|Silk/i.test(this.userAgent);
  }

  public isDesktop(): boolean {
    return !this.isMobile() && !this.isTablet();
  }

  public getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (this.isTablet()) return 'tablet';
    if (this.isMobile()) return 'mobile';
    return 'desktop';
  }

  public getOS(): string {
    if (/Windows NT/i.test(this.userAgent)) return 'Windows';
    if (/Macintosh|Mac OS X/i.test(this.userAgent)) return 'macOS';
    if (/Linux/i.test(this.userAgent)) return 'Linux';
    if (/Android/i.test(this.userAgent)) return 'Android';
    if (/iPhone|iPad|iPod/i.test(this.userAgent)) return 'iOS';
    return 'Unknown';
  }

  public getBrowser(): string {
    if (/Chrome\/\d+/i.test(this.userAgent)) return 'Chrome';
    if (/Firefox\/\d+/i.test(this.userAgent)) return 'Firefox';
    if (/Safari\/\d+/i.test(this.userAgent) && !/Chrome/i.test(this.userAgent)) return 'Safari';
    if (/Edg\/\d+/i.test(this.userAgent)) return 'Edge';
    if (/MSIE|Trident/i.test(this.userAgent)) return 'Internet Explorer';
    return 'Unknown';
  }

  public getInfo() {
    return {
      userAgent: this.userAgent,
      deviceType: this.getDeviceType(),
      os: this.getOS(),
      browser: this.getBrowser(),
    };
  }
}


export class DeviceManager {

  public readonly onAppUrlOpenWithUrl = new GameDispatcher(this);
  public readonly onActivateEvent = new GameDispatcher(this);
  public readonly onDeactivateEvent = new GameDispatcher(this);
  public readonly onWindowResize = new GameDispatcher(this);
  public readonly onExit = new GameDispatcher(this);

  private _deviceLanguageCode = 'en';
  private _isAndroid = false;
  private _isIOS = false;
  private _isTablet = false;
  private _isDesktop = false;
  private _isMobile = false;
  private _deviceName = '';
  private _deviceModel = '';
  private _deviceManufacturer = '';
  private _deviceOSName = '';
  private _deviceOperatingSystem = '';
  private _deviceOSVersion = '';
  private _appOpenWithUrl = '';
  private _safeAreaTop = 0;
  private _safeAreaBottom = 0;
  private _resizeDebounceTimerId: any = null;
  private _resizeDebounceTime = 200; // ms
  private _lastWindowWidth = 0;
  private _lastWindowHeight = 0;

  public async init() {
    await this.waitForDOMReady();

    let deviceDetector = new DeviceDetectorService();
    this._isTablet = deviceDetector.isTablet();
    this._isDesktop = deviceDetector.isDesktop();
    this._isMobile = deviceDetector.isMobile();

    const languageCode = await Device.getLanguageCode();
    this._deviceLanguageCode = languageCode.value;

    const deviceInfo = await Device.getInfo();
    this._deviceName = deviceInfo.name || '';
    this._deviceModel = deviceInfo.model;
    this._deviceManufacturer = deviceInfo.manufacturer;
    this._deviceOSName = deviceInfo.operatingSystem;
    this._deviceOSVersion = deviceInfo.osVersion;
    this._deviceOperatingSystem = deviceInfo.operatingSystem;

    this._isAndroid = deviceInfo.platform === 'android';
    this._isIOS = deviceInfo.platform === 'ios';

    let launchData = await App.getLaunchUrl();
    this._appOpenWithUrl = launchData?.url || '';
    if (this._appOpenWithUrl) {
      this.onAppUrlOpenWithUrl.dispatch();
    }
    void App.addListener('appUrlOpen', data => {
      this._appOpenWithUrl = data.url;
      this.onAppUrlOpenWithUrl.dispatch();
    });

    if (this._isDesktop) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.onActivate();
        } else {
          this.onDeactivate();
        }
      });
    } else {
      void App.addListener('pause', () => this.onDeactivate());
      void App.addListener('resume', () => this.onActivate());
    }

    await this.updateSafeArea();

    this._lastWindowWidth = window.innerWidth;
    this._lastWindowHeight = window.innerHeight;
    window.addEventListener('resize', ()=>{
      this.checkWindowResize();
    });
    window.addEventListener('orientationchange', ()=>{
      this.checkWindowResize();
    });
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.checkWindowResize();
      }
    });
  }

  get isPortrait() {
    return window.innerHeight > window.innerWidth;
  }

  get isLandscape() {
    return window.innerWidth > window.innerHeight;
  }

  get devicePixelRatio() {
    return window.devicePixelRatio || 1;
  }

  private async checkWindowResize() {
    clearTimeout(this._resizeDebounceTimerId);
    const onWindowResize = async () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      if (this._lastWindowWidth != newWidth || this._lastWindowHeight != newHeight) {
        this._lastWindowWidth = newWidth;
        this._lastWindowHeight = newHeight;
        await this.updateSafeArea();
        this.onWindowResize.dispatch();
      }
    }
    this._resizeDebounceTimerId = setTimeout(onWindowResize, this._resizeDebounceTime); // задержка 200 мс, можно настроить
  }

  private async updateSafeArea(){
    if (this.isMobile) {
      const safeArea = await SafeArea.getSafeAreaInsets();
      this._safeAreaTop = safeArea.insets.top;
      this._safeAreaBottom = safeArea.insets.bottom;
    }
    else {
      const style = getComputedStyle(document.documentElement);
      const top = style.getPropertyValue('--safe-area-inset-top');
      const bottom = style.getPropertyValue('--safe-area-inset-bottom');
      this._safeAreaTop = parseInt(top) || 0;
      this._safeAreaBottom = parseInt(bottom) || 0;
    }
  }

  get safeAreaTop(): number {
    return this._safeAreaTop;
  }

  get safeAreaBottom(): number {
    return this._safeAreaBottom;
  }

  private onActivate() {
    this.onActivateEvent.dispatch();
  }

  private onDeactivate() {
    this.onDeactivateEvent.dispatch();
  }

  public forceExit() {
    this.onExit.dispatch();
    if (this._isMobile) {
      App.exitApp();
    }
  }

  private async waitForDOMReady() {
    if (document.readyState === 'complete' || document.readyState === 'interactive') return;
    return new Promise<void>((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    });
  }

  public get appOpenWithUrl() { return this._appOpenWithUrl; }
  public get deviceLanguageCode() { return this._deviceLanguageCode; }
  public get deviceName() { return this._deviceName; }
  public get deviceOperatingSystem() { return this._deviceOperatingSystem; }
  public get deviceModel() { return this._deviceModel; }
  public get deviceManufacturer() { return this._deviceManufacturer; }
  public get deviceOSName() { return this._deviceOSName; }
  public get deviceOSVersion() { return this._deviceOSVersion; }
  public get isTablet() { return this._isTablet; }
  public get isDesktop() { return this._isDesktop; }
  public get isMobile() { return this._isMobile; }
  public get isAndroid() { return this._isAndroid; }
  public get isIOS() { return this._isIOS; }

  public async getDeviceId() {
    return await Device.getId();
  }

  public async getPersistentSecureDeviceId() {
    let { value } = await SecureStoragePlugin.get({ key: 'deviceId' }).catch(() => ({ value: null }));
    if (!value) {
      let DeviceId = await this.getDeviceId();
      let id = DeviceId.identifier;
      await SecureStoragePlugin.set({ key: 'deviceId', value: id });
      return id;
    }
    return value;
  }

  public async getDeviceInfo() {
    return await Device.getInfo();
  }

  public async getLanguageCode() {
    return await Device.getLanguageCode();
  }
}
